import { useEffect, useState } from 'react';

/**
 * Returns the index of the currently visible slide in the carousel.
 */
export const useDomCarouselIndex = ({
  containerRef
}: {
  containerRef?: React.RefObject<HTMLElement | null>;
}) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;
    const update = () => {
      const slides = Array.from(container.querySelectorAll('[aria-roledescription="slide"]'));
      const visibleSlideIndex = slides.findIndex(slide => slide.getAttribute('aria-hidden') === 'false');
      setIndex(visibleSlideIndex === -1 ? 0 : visibleSlideIndex);
    };

    // Initial
    update();
    const observer = new MutationObserver(update);

    // Observe changes to 'aria-hidden' attributes on slides
    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [containerRef]);
  return index;
};

/**
 * Returns the `top` value in px for positioning the mobile dot navigation container below
 * the image within the carousel.
 */
export const useImageBottomOffset = ({
  containerRef,
  imageBottomOffsetPx
}: {
  containerRef?: React.RefObject<HTMLElement | null>;
  imageBottomOffsetPx: number;
}) => {
  const [top, setTop] = useState<number | null>(null);
  useEffect(() => {
    const container = containerRef?.current;
    const img = container?.querySelector<HTMLImageElement>('img');
    if (!img) return;
    const update = () => {
      const rect = img.getBoundingClientRect();
      setTop(rect.height + imageBottomOffsetPx);
    };

    // Initial + when image loads
    if (img.complete) {
      update();
    } else {
      img.addEventListener('load', update);
    }

    // Recalculate on resize
    window.addEventListener('resize', update);

    // eslint-disable-next-line consistent-return
    return () => {
      img.removeEventListener('load', update);
      window.removeEventListener('resize', update);
    };
  }, [containerRef, imageBottomOffsetPx]);
  return top;
};